import React from 'react';
import { Card } from '../components/ui';

export default function SchoolExamReport({ items, formatDate }) {
  if (!items || items.length === 0) return null;

  // Group exams by term
  const examsByTerm = items.reduce((acc, exam) => {
    const termKey = exam.term || 1;
    if (!acc[termKey]) acc[termKey] = [];
    acc[termKey].push(exam);
    return acc;
  }, {});

  const terms = Object.keys(examsByTerm).sort((a, b) => Number(a) - Number(b));

  const getScoreColor = (score, maxScore) => {
    if (score == null || maxScore == null) return '#64748b';
    const percentage = (score / maxScore) * 100;
    if (percentage >= 85) return '#22c55e';
    if (percentage >= 70) return '#3b82f6';
    if (percentage >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const TermSection = ({ term, exams }) => {
    // Group exams by subject and exam number
    const examsBySubject = {};
    const examNumbers = new Set();
    
    exams.forEach(exam => {
      const subject = exam.subject_label || 'Diğer';
      const examNum = exam.exam_number || 1;
      examNumbers.add(examNum);
      
      if (!examsBySubject[subject]) {
        examsBySubject[subject] = {};
      }
      examsBySubject[subject][examNum] = exam;
    });

    const sortedExamNumbers = Array.from(examNumbers).sort((a, b) => a - b);
    const subjects = Object.keys(examsBySubject).sort();

    // Calculate average for each subject
    const getSubjectAverage = (subjectExams) => {
      const scores = Object.values(subjectExams)
        .map(e => e.score)
        .filter(s => s != null);
      if (scores.length === 0) return null;
      return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2);
    };

    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ 
          fontSize: 15, 
          fontWeight: 700, 
          color: '#0F1E56', 
          marginBottom: 12,
          padding: '10px 14px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{term}. Dönem</span>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
            {exams.length} sınav kaydı
          </span>
        </div>

        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {/* Table Header */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: `200px repeat(${sortedExamNumbers.length}, 85px) 90px`,
            gap: 8,
            minWidth: `calc(200px + ${sortedExamNumbers.length} * 85px + 90px + ${(sortedExamNumbers.length + 1) * 8}px)`,
            padding: '10px 14px',
            background: '#f8fafc',
            borderRadius: 10,
            marginBottom: 6,
            fontSize: 12,
            fontWeight: 700,
            color: '#475569',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ paddingLeft: 4 }}>Ders</div>
            {sortedExamNumbers.map(num => (
              <div key={num} style={{ textAlign: 'center' }}>{num}. Sınav</div>
            ))}
            <div style={{ textAlign: 'center' }}>Ort.</div>
          </div>

          {/* Table Rows */}
          <div style={{ display: 'grid', gap: 6 }}>
          {subjects.map((subject, idx) => {
            const subjectExams = examsBySubject[subject];
            const avg = getSubjectAverage(subjectExams);
            
            return (
              <div 
                key={subject}
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: `200px repeat(${sortedExamNumbers.length}, 85px) 90px`,
                  minWidth: `calc(200px + ${sortedExamNumbers.length} * 85px + 90px + ${(sortedExamNumbers.length + 1) * 8}px)`,
                  gap: 8,
                  padding: '12px 14px',
                  background: '#fff',
                  borderRadius: 10,
                  border: '1px solid #e5e7eb',
                  alignItems: 'center',
                  transition: 'all 0.2s ease',
                  ':hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }
                }}
              >
                <div style={{ 
                  fontWeight: 600, 
                  fontSize: 14,
                  color: '#0f172a',
                  paddingLeft: 4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {subject}
                </div>
                
                {sortedExamNumbers.map(num => {
                  const exam = subjectExams[num];
                  if (!exam || exam.score == null) {
                    return (
                      <div key={num} style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 13 }}>
                        -
                      </div>
                    );
                  }
                  
                  const scoreColor = getScoreColor(exam.score, exam.max_score || 100);
                  return (
                    <div 
                      key={num} 
                      style={{ 
                        textAlign: 'center',
                        padding: '6px 8px',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 700,
                        color: scoreColor,
                        background: `${scoreColor}10`,
                        border: `1px solid ${scoreColor}30`
                      }}
                      title={exam.date ? formatDate(exam.date) : ''}
                    >
                      {exam.score}
                    </div>
                  );
                })}
                
                <div style={{ 
                  textAlign: 'center',
                  padding: '6px 8px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#0F1E56',
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0'
                }}>
                  {avg || '-'}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card title="Okul Sınavları">
      {terms.map((term) => (
        <TermSection key={term} term={term} exams={examsByTerm[term]} />
      ))}
      <div style={{ marginTop: 12, fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
        Toplam {items.length} sınav kaydı
      </div>
    </Card>
  );
}
